<template>
    <BannerFooterOptions
        v-if="userCanManageMasterTemplates"
        :model-value="optionsVisible"
        :options="footerOptions"
        @update:modelValue="emit('updateOptionsVisible', $event)"
    />

    <MasterTemplateReplaceButton ref="replace" :master-template-id="masterTemplate._id" />
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { Modal } from "view-ui-plus";

import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import type { MasterTemplate } from "hoxton-types";
import { USER_ACTIONS } from "hoxton-shared/enums/user";

import BannerFooterOptions from "@/components/Previews/BannerFooter/BannerFooterOptions.vue";
import MasterTemplateReplaceButton from "@/components/Campaign/MasterTemplates/MasterTemplateReplaceButton.vue";

import { apolloClient } from "@/apollo";
import QueryMasterTemplateDownloadUrl from "@/apollo/queries/MasterTemplateDownloadUrl.gql";
import { userCan } from "@/services/Auth";
import downloadFile from "@/utils/downloadFile";

const route = useRoute();
const replace = useTemplateRef("replace");

const clientId = route.params.clientId as string;

const {
    resizeMode = undefined,
    optionsVisible,
    masterTemplate
} = defineProps<{
    resizeMode?: boolean;
    masterTemplate: MasterTemplate;
    optionsVisible: boolean;
}>();

const emit = defineEmits<{
    (e: "remove"): void;
    (e: "updateOptionsVisible" | "toggleResizeMode", value: boolean): void;
}>();

const userCanManageMasterTemplates = computed(() => userCan(USER_ACTIONS.canManageMasterTemplates, { clientId }));
const isAE = computed(() => masterTemplate.adType === TemplateType.AE);
const isInDesign = computed(() => masterTemplate.adType === TemplateType.INDESIGN);
const isHTML5 = computed(() => masterTemplate.adType === TemplateType.HTML5);

const isChildResize = computed(() => !!masterTemplate.parentId);

const footerOptions = computed(() => {
    const options = [];

    if (isAE.value || isInDesign.value) {
        return [
            {
                id: "delete-button",
                label: "Delete",
                icon: "md-trash",
                onClick: deleteTemplatePrompt
            }
        ];
    }

    if (isHTML5.value && resizeMode !== undefined) {
        options.push({
            id: "resize-button",
            label: resizeMode ? "Exit Resize Mode" : "Resize Mode",
            icon: "md-expand",
            onClick: toggleResizeMode
        });
    }

    if (isChildResize.value) {
        options.push({
            id: "delete-button",
            label: "Delete",
            icon: "md-trash",
            onClick: deleteTemplatePrompt
        });

        return options;
    }

    options.push({
        id: "download-button",
        label: "Download Template",
        icon: "md-cloud-download",
        onClick: onDownload
    });

    options.push({
        id: "replace-button",
        label: "Replace Template",
        icon: "md-cloud-upload",
        onClick: onReplace
    });

    options.push({
        id: "delete-button",
        label: "Delete",
        icon: "md-trash",
        onClick: deleteTemplatePrompt
    });

    return options;
});

function toggleResizeMode() {
    emit("toggleResizeMode", !resizeMode);
}

function hideFooterOptions() {
    emit("updateOptionsVisible", false);
}

async function onDownload() {
    if (!apolloClient) {
        throw new Error("Apollo client is not initialized");
    }

    const { data } = await apolloClient.query({
        query: QueryMasterTemplateDownloadUrl,
        variables: {
            masterTemplateId: masterTemplate._id
        }
    });

    downloadFile(data.masterTemplateDownloadUrl, `${masterTemplate.name}.zip`);

    hideFooterOptions();
}

async function onReplace() {
    hideFooterOptions();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    replace.value.remoteClick();
}

function deleteTemplatePrompt() {
    hideFooterOptions();

    Modal.confirm({
        title: "Are you sure?",
        content:
            "This template may be used in other campaigns. All ads using this template will be deleted. This is permanent and can not be undone.",
        okText: "Delete",
        onOk: () => emit("remove"),
        cancelText: "Cancel",
        width: 300
    });
}
</script>
